import {isFeatureEnabled} from '@github-ui/feature-flags'
import {createContext, type PropsWithChildren, use, useCallback, useMemo, useState} from 'react'
import {useSearchParams} from '@github-ui/use-navigate'

interface SearchTypeContextType {
  type: string | null
  setType: (type: string | null) => void
}

const SearchTypeContext = createContext<SearchTypeContextType | undefined>(undefined)

export function useSearchType() {
  const context = use(SearchTypeContext)
  if (!context) throw new Error('useSearchType must be used within a SearchTypeProvider')
  return context
}

export function SearchTypeProvider({children}: PropsWithChildren) {
  const [searchParams] = useSearchParams()
  const hideGitHubModels = isFeatureEnabled('hide_github_models_ui')

  // Suppress the `models` type at the source — before first render (including SSR) and on
  // every update — so no consumer (Filters, ResultListHeader, FilterContext, etc.) can surface
  // Models UI when `hide_github_models_ui` is enabled, regardless of the `type` URL param.
  const normalizeType = useCallback(
    (value: string | null) => (hideGitHubModels && value === 'models' ? null : value),
    [hideGitHubModels],
  )

  const [type, setTypeState] = useState<string | null>(() => normalizeType(searchParams.get('type')))
  const setType = useCallback((value: string | null) => setTypeState(normalizeType(value)), [normalizeType])

  const value = useMemo(() => ({type, setType}), [type, setType])
  return <SearchTypeContext value={value}>{children}</SearchTypeContext>
}
